import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiChevronDown } from "react-icons/fi";
import "./GetInTouch.css";
import { useTranslation } from "react-i18next";
import { getCourses } from "../../../../api/courses";
import { createContactUsRequest } from "../../../../api/contactUs";
import { scrollToElementById } from "../../../../utils/scrollToId";

const EXPERIENCE_OPTIONS = [
  "0-1 years",
  "1-3 years",
  "3-5 years",
  "5-10 years",
  "10+ years",
];

const GetInTouch = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    location: "",
    courseId: "",
    yearsOfExperience: "",
  });

  useEffect(() => {
    let isMounted = true;
    const loadCourses = async () => {
      try {
        const data = await getCourses({ language: String(i18n.language || "en").toLowerCase() });
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setCourses(list);
        setForm((prev) => ({
          ...prev,
          courseId: prev.courseId || String(list[0]?.id || ""),
        }));
      } catch {
        if (!isMounted) return;
        setCourses([]);
      }
    };
    loadCourses();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  useEffect(() => {
    if (location.hash !== "#contact-form") return;
    const frame = window.requestAnimationFrame(() => {
      scrollToElementById("contact-form");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!String(form.name || "").trim()) {
      setError("Name is required.");
      return;
    }
    if (!String(form.email || "").trim()) {
      setError("Email is required.");
      return;
    }
    if (!String(form.location || "").trim()) {
      setError("Location is required.");
      return;
    }
    if (!String(form.courseId || "").trim()) {
      setError("Please choose a course.");
      return;
    }
    if (!String(form.yearsOfExperience || "").trim()) {
      setError("Please choose years of experience.");
      return;
    }

    setSubmitting(true);
    try {
      await createContactUsRequest({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        phoneNumber: String(form.phoneNumber || "").trim() || null,
        location: String(form.location || "").trim(),
        courseId: Number(form.courseId),
        yearsOfExperience: String(form.yearsOfExperience || "").trim(),
      });
      setSuccess("Your request has been sent successfully.");
      setForm({
        name: "",
        email: "",
        phoneNumber: "",
        location: "",
        courseId: String(courses[0]?.id || ""),
        yearsOfExperience: "",
      });
    } catch (submitError) {
      setError(submitError?.response?.data?.message || "Failed to send request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="academyContact" aria-label="Get in touch">
      <div className="academyContact-inner">
        <div className="academyContact-left">
          <span className="academyContact-tag">LET&apos;S GET IN TOUCH</span>

          <h2 className="academyContact-title">
            Do you have <span>more question?</span>
          </h2>

          <p className="academyContact-text">
Whether you need more information about our products, services, or project support, our team is here to help. Fill out the form and we’ll get back to you as soon as possible with the answers and guidance you need.
          </p>
        </div>

        <div className="academyContact-right">
          <h3 className="academyContact-formTitle">Fill out the form</h3>

          <form className="academyContact-form" onSubmit={handleSubmit}>
            <div className="academyContact-row academyContact-rowHalf">
              <input
                type="text"
                placeholder="Your name"
                aria-label="Your name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
              <input
                type="email"
                placeholder="Your email"
                aria-label="Your email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>

            <div className="academyContact-row">
              <input
                type="text"
                placeholder="Phone number (optional)"
                aria-label="Phone number optional"
                value={form.phoneNumber}
                onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
              />
            </div>

            <div className="academyContact-row">
              <input
                type="text"
                placeholder="Location"
                aria-label="Location"
                value={form.location}
                onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
              />
            </div>

            <div className="academyContact-row academyContact-selectLike">
              <select
                value={form.courseId}
                onChange={(e) => setForm((p) => ({ ...p, courseId: e.target.value }))}
                aria-label="Course you are interested in"
              >
                {courses.length ? (
                  courses.map((course) => (
                    <option key={course.id} value={String(course.id)}>
                      {course.title}
                    </option>
                  ))
                ) : (
                  <option value="">Course you are interested in</option>
                )}
              </select>
              <FiChevronDown aria-hidden />
            </div>

            <div className="academyContact-row academyContact-selectLike">
              <select
                value={form.yearsOfExperience}
                onChange={(e) => setForm((p) => ({ ...p, yearsOfExperience: e.target.value }))}
                aria-label="Years of experience"
              >
                <option value="">Years of experience</option>
                {EXPERIENCE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <FiChevronDown aria-hidden />
            </div>

            {error ? <p className="academyContact-message academyContact-messageError">{error}</p> : null}
            {success ? <p className="academyContact-message academyContact-messageSuccess">{success}</p> : null}

            <div className="academyContact-action">
              <button type="submit" className="academyContact-submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Request information"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default GetInTouch;